import * as Sentry from '@sentry/react';
import ToastService from '../services/toast-service';

const GLOBAL_ERROR_MSG = 'An unexpected error has occurred. '
	+ 'If you notice your application acting oddly, please refresh the page.';

export const SKIP_STATUSES = [401, 403, 404, 422];

const isDevelopment = () => window.pixwel?.env === 'development';

export function handleGlobalError(exception, cause) {
	if(!isDevelopment()) {
		Sentry.captureException(exception);
	}

	ToastService.create('error', GLOBAL_ERROR_MSG, { duration: 10000 });

	// Log with cause if available
	console.error(...(cause ? [cause, exception] : [exception]));
}

export function captureError(e, message) {
	// Skip entirely for 401 — handled globally by the API interceptor (redirect to login).
	// Processing them here would duplicate toasts and flood console/Sentry logs.
	const status = e?.status ?? e?.response?.status;
	if(status === 401) {
		return;
	}

	// Skip Sentry for development environment and expected HTTP errors (403, 404, 422)
	if(!isDevelopment() && (!status || !SKIP_STATUSES.includes(status))) {
		Sentry.captureException(e);
	}

	console.error(message || 'Error:', e);

	// Extract error message for toast notification with consistent toastId
	const toastOptions = { toastId: 'capture-error' };

	if(message) {
		ToastService.create('error', message, toastOptions);
		return;
	}

	if(typeof e === 'string') {
		ToastService.create('error', e, toastOptions);
		return;
	}

	if(e?.response?.data) {
		const errorData = e.response.data;
		let errorMessage = '';

		if(errorData.errors) {
			errorMessage = Object.values(errorData.errors).join('\n');
		}
		else if(typeof errorData === 'object') {
			errorMessage = Object.keys(errorData)
				.filter((key) => Array.isArray(errorData[key]))
				.map((key) => errorData[key].flat().join('\n'))
				.filter(Boolean)
				.join('\n');
		}

		if(errorMessage) {
			ToastService.create('error', errorMessage, toastOptions);
			return;
		}
	}

	if(typeof e?.message === 'string') {
		ToastService.create('error', e.message, toastOptions);
	}
}